import type {
  ListNodeTagType,
  ListType,
  SerializedListItemNode,
  SerializedListNode as LexicalSerializedListNode,
} from '@lexical/list';
import type {
  DOMExportOutput,
  LexicalCommand,
  LexicalEditor,
  LexicalNode,
} from 'lexical';

import {
  $getNearestNodeOfType,
} from '@lexical/utils';

import {
  $createListItemNode,
  $createListNode,
  $getListDepth,
  $handleListInsertParagraph,
  $insertList,
  $isListItemNode,
  $isListNode,
  $removeList,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode as PhxListItemNode,
  ListNode as PhxListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import {
  $getSelection,
  $isRangeSelection,
} from 'lexical';

import { getListMarker } from './listStyles';

// Re-export core list primitives so existing imports continue to work,
// but delegate behavior to @lexical/list. We additionally augment the
// underlying ListNode with a custom __phxVariant field for styling.
export {
  $createListItemNode,
  $createListNode,
  $getListDepth,
  $handleListInsertParagraph,
  $insertList as insertList,
  $isListItemNode,
  $isListNode,
  $removeList as removeList,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  PhxListItemNode,
  PhxListNode,
};

export type {
  ListNodeTagType,
  ListType,
  SerializedListItemNode,
  LexicalSerializedListNode,
};

// CHANGE_LIST_TYPE_COMMAND does not exist in @lexical/list; keep a local
// command definition for backwards-compatibility where it is still imported.
import { createCommand } from 'lexical';

export const CHANGE_LIST_TYPE_COMMAND: LexicalCommand<ListType> = createCommand(
  'CHANGE_LIST_TYPE_COMMAND',
);

// ---------------------------------------------------------------------------
// Phoenix-specific list variants
// ---------------------------------------------------------------------------

export type PhxListVariant =
  | 'phx_alpha'
  | 'phx_number'
  | 'legacy_alpha'
  | 'legacy_number'
  | 'phx_bullet'
  | 'custom';

// Extend the serialized ListNode shape to carry our variant and list-style-type.
export type SerializedPhxListNode = LexicalSerializedListNode & {
  phxVariant?: PhxListVariant;
  phxListStyleType?: string;
};

// TypeScript module augmentation so PhxListNode (ListNode) instances expose
// getPhxVariant/setPhxVariant and carry __phxVariant at runtime.
declare module '@lexical/list' {
  interface ListNode {
    __phxVariant?: PhxListVariant;
    getPhxVariant?(): PhxListVariant;
    setPhxVariant?(variant: PhxListVariant): this;
  }
}

/**
 * From a ListNode, find the root list (topmost ListNode in the hierarchy) and
 * this list's depth relative to that root (0 = root, 1 = first nested, etc.).
 * Only the root list should carry __phxVariant; nested lists are styled by root variant + level.
 */
export function getRootListAndLevel(
  listNode: PhxListNode,
): { root: PhxListNode; level: number } {
  let root: PhxListNode = listNode;
  let current: LexicalNode | null = listNode;
  while (current) {
    const parent: LexicalNode | null = current.getParent();
    if (parent && $isListNode(parent)) {
      root = parent as PhxListNode;
    }
    current = parent;
  }
  let level = 0;
  current = listNode;
  while (current && current !== root) {
    const parent: LexicalNode | null = current.getParent();
    if (parent && $isListNode(parent)) {
      level += 1;
    }
    current = parent;
  }
  return { root, level };
}

// Runtime augmentation of the underlying ListNode prototype so that we can
// store a lightweight __phxVariant flag and ensure it survives DOM/JSON
// serialization round‑trips.
const listNodeProto = (PhxListNode as any).prototype;

if (listNodeProto != null && !listNodeProto.__phxVariantPatched) {
  const getDomListTypeForVariant = (variant: PhxListVariant): string | null => {
    switch (variant) {
      case 'phx_number':
        return 'phx_number';
      case 'phx_alpha':
        return 'phx_alpha';
      case 'legacy_number':
        return 'legacy_number';
      case 'legacy_alpha':
        return 'legacy_alpha';
      case 'phx_bullet':
        return 'phx_bullet';
      default:
        return null;
    }
  };

  const originalExportJSON =
    listNodeProto.exportJSON as () => LexicalSerializedListNode;
  const originalUpdateFromJSON = listNodeProto.updateFromJSON as (
    serializedNode: LexicalSerializedListNode,
  ) => PhxListNode;
  const originalExportDOM = listNodeProto.exportDOM as (
    editor: LexicalEditor,
  ) => DOMExportOutput;
  const originalCreateDOM = listNodeProto.createDOM as (
    config: unknown,
    editor: LexicalEditor,
  ) => HTMLElement;
  const originalUpdateDOM = listNodeProto.updateDOM as (
    prevNode: PhxListNode,
    dom: HTMLElement,
    config: unknown,
  ) => boolean;

  listNodeProto.__phxVariantPatched = true;

  listNodeProto.getPhxVariant = function getPhxVariant(): PhxListVariant {
    // Default lists without an explicit variant to "custom" so that inline
    // styles are preserved unless a Phoenix variant is chosen.
    return this.__phxVariant ?? 'custom';
  };

  listNodeProto.setPhxVariant = function setPhxVariant(
    variant: PhxListVariant,
  ): typeof this {
    const self = this.getWritable();
    self.__phxVariant = variant;
    return self;
  };

  listNodeProto.exportJSON = function exportJSONPatched(): LexicalSerializedListNode {
    const json = originalExportJSON.call(this) as SerializedPhxListNode;
    const { root, level } = getRootListAndLevel(this as PhxListNode);
    const variant =
      level === 0
        ? (root.getPhxVariant ? root.getPhxVariant() : (root as any).__phxVariant)
        : undefined;
    const listStyleType = level === 0 ? (root as any).__phxListStyleType : undefined;

    if (variant) {
      json.phxVariant = variant;
    }
    if (listStyleType) {
      json.phxListStyleType = listStyleType;
    }

    return json;
  };

  listNodeProto.updateFromJSON = function updateFromJSONPatched(
    serializedNode: LexicalSerializedListNode,
  ): PhxListNode {
    const node = originalUpdateFromJSON.call(this, serializedNode);
    const phxSerialized = serializedNode as SerializedPhxListNode;
    if (phxSerialized.phxVariant) {
      node.__phxVariant = phxSerialized.phxVariant;
    }
    if (phxSerialized.phxListStyleType) {
      (node as any).__phxListStyleType = phxSerialized.phxListStyleType;
    }

    return node;
  };

  listNodeProto.exportDOM = function exportDOMPatched(
    editor: LexicalEditor,
    node?: PhxListNode,
  ): DOMExportOutput {
    // When Lexical HTML serializer calls registeredNode.exportDOM(editor, target), "this" is the
    // class, not the node. Use the node argument when present so we read the correct variant.
    const listNode = (node != null && typeof (node as any).getParent === 'function')
      ? node
      : (this as unknown as PhxListNode);
    const { root, level } = getRootListAndLevel(listNode);
    const rootVariant: PhxListVariant =
      (root.getPhxVariant ? root.getPhxVariant() : (root as any).__phxVariant) ?? 'custom';
    const listStyleType = (root as any).__phxListStyleType;
    const result = originalExportDOM.call(listNode, editor) as DOMExportOutput;
    const element = result.element;

    if (!element) {
      return result;
    }

    const el = element as HTMLElement;
    if (listStyleType && level === 0) {
      el.style.listStyleType = listStyleType;
    } else if (rootVariant === 'custom') {
      if (level === 0) {
        el.setAttribute('data-list-type', 'custom');
      } else {
        el.removeAttribute('data-list-type');
      }
    } else {
      const listType = getDomListTypeForVariant(rootVariant);
      if (listType) {
        if (level === 0) {
          el.setAttribute('data-list-type', listType);
        } else {
          el.removeAttribute('data-list-type');
        }
        try {
          const marker = getListMarker(listType, level);
          el.style.listStyleType = marker;
        } catch {
          // Fall back to theme/browser default.
        }
      }
    }

    return result;
  };

  listNodeProto.createDOM = function createDOMPatched(
    config: unknown,
    editor: LexicalEditor,
  ): HTMLElement {
    const dom = originalCreateDOM.call(this, config, editor) as HTMLElement;
    const { root, level } = getRootListAndLevel(this as PhxListNode);
    const rootVariant: PhxListVariant =
      (root.getPhxVariant ? root.getPhxVariant() : (root as any).__phxVariant) ?? 'custom';
    const listStyleType = (root as any).__phxListStyleType;
    if (listStyleType && level === 0) {
      (dom as HTMLElement).style.listStyleType = listStyleType;
    } else if (rootVariant === 'custom') {
      if (level === 0) {
        dom.setAttribute('data-list-type', 'custom');
      } else {
        dom.removeAttribute('data-list-type');
      }
    } else {
      const listTypeAttr = getDomListTypeForVariant(rootVariant);

      if (listTypeAttr) {
        if (level === 0) {
          dom.setAttribute('data-list-type', listTypeAttr);
        } else {
          dom.removeAttribute('data-list-type');
        }
        try {
          const marker = getListMarker(listTypeAttr, level);
          (dom as HTMLElement).style.listStyleType = marker;
        } catch {
          // If this fails for any reason, fall back to theme/browser defaults.
        }
      } else {
        dom.removeAttribute('data-list-type');
      }
    }

    return dom;
  };

  listNodeProto.updateDOM = function updateDOMPatched(
    prevNode: PhxListNode,
    dom: HTMLElement,
    config: unknown,
  ): boolean {
    const result = originalUpdateDOM.call(this, prevNode, dom, config);
    const { root, level } = getRootListAndLevel(this as PhxListNode);
    const rootVariant: PhxListVariant =
      (root.getPhxVariant ? root.getPhxVariant() : (root as any).__phxVariant) ?? 'custom';
    const listStyleType = (root as any).__phxListStyleType;
    if (listStyleType && level === 0) {
      (dom as HTMLElement).style.listStyleType = listStyleType;
    } else if (rootVariant === 'custom') {
      if (level === 0) {
        dom.setAttribute('data-list-type', 'custom');
      } else {
        dom.removeAttribute('data-list-type');
      }
    } else {
      const listTypeAttr = getDomListTypeForVariant(rootVariant);

      if (listTypeAttr) {
        if (level === 0) {
          dom.setAttribute('data-list-type', listTypeAttr);
        } else {
          dom.removeAttribute('data-list-type');
        }
        try {
          const marker = getListMarker(listTypeAttr, level);
          (dom as HTMLElement).style.listStyleType = marker;
        } catch {
          // Ignore and keep existing styles
        }
      } else {
        dom.removeAttribute('data-list-type');
      }
    }

    return result;
  };
}

/** Map data-list-type attribute (from DOM/HTML) to PhxListVariant for import. */
export function getPhxVariantFromDataListType(attr: string | null): PhxListVariant | null {
  if (!attr) return null;
  switch (attr) {
    case 'number':
    case 'phx_number':
      return 'phx_number';
    case 'alpha':
    case 'phx_alpha':
      return 'phx_alpha';
    case 'legacy_number':
      return 'legacy_number';
    case 'legacy_alpha':
      return 'legacy_alpha';
    case 'bullet':
    case 'phx_bullet':
      return 'phx_bullet';
    case 'custom':
      return 'custom';
    default:
      return null;
  }
}

/**
 * Helper to set the Phoenix list variant on the root list(s) that contain the
 * current selection. Only the root list carries the variant; nested lists
 * are styled by root variant + level. Must be called from within editor.update.
 */
export function $setPhxListVariantOnSelection(variant: PhxListVariant): void {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    return;
  }

  const nodes = selection.getNodes();
  const rootLists = new Set<PhxListNode>();

  for (let i = 0; i < nodes.length; i++) {
    const node: LexicalNode = nodes[i];
    const listNode = $getNearestNodeOfType(node, PhxListNode) as PhxListNode | null;

    if (listNode != null) {
      const { root } = getRootListAndLevel(listNode);
      rootLists.add(root);
    }
  }

  rootLists.forEach((rootNode) => {
    if (typeof rootNode.setPhxVariant === 'function') {
      rootNode.setPhxVariant(variant);
    } else {
      (rootNode as any).__phxVariant = variant;
    }
  });
}
